import React from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {useNavigate, useParams } from "react-router";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings, Settings2, X } from "lucide-react";
import UpdateIntakeFlagModal from "../components/Intakes/ConfirmIntakeFlagUpdateModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import MigrationStatusChip from "../components/common/MigrationStatusChip";
import { MigrationFlagStatus, MigrationFlagStatusSelectOptions } from "../models/Flags/MigrationFlagStatus";
import { Intake } from "../models/Intake";
import { IntakeFile } from "../models/IntakeFile";
import IntakesRepository from "../repositories/IntakesRepository";
import {UserRole} from "../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import {QUERY_KEYS} from "../constants/QueryKeys";
import { IntakeEmptyFoldersTable } from "../components/IntakeEmptyFolders/IntakeEmptyFoldersTable";
import {CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS, CreateDeleteFilesRequestTable} from "../components/common/File/CreateDeleteFilesRequestTable";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import {IntakeFilesTable, IntakeFilesTableRef} from "../components/IntakeFiles/IntakeFilesTable";
import {ConflictResolveView} from "../components/common/Table/File/ConflictResolveView";
import { DeleteIntakeFilesStatusTable } from "../components/IntakeFiles/DeleteIntakeFilesStatusTable";
import {ExportIntakeFilesToCsvStatusTable} from "../components/IntakeFiles/ExportIntakeFilesToCsvStatusTable";
import {ActiveJobsExplorer} from "../models/Jobs/ActiveJobsExplorer";
import MigrationEntityShortStatistics from "../components/common/MigrationEntity/MigrationEntityShortStatistics";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import { useDetailBreadcrumb } from "../contexts/DetailBreadcrumbContext";
import { cn } from "@/lib/utils";
import { FlaggedPathResolveView } from "../components/common/Table/File/FlaggedPathResolveView";
import { MigrationType } from "../models/Statistics/MigrationType";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface IntakePageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

export const IntakePage: React.FC<IntakePageProps> = ({
  userRole,
  isAssignedUser
}: IntakePageProps) => {
  const requiresReason = useRequiresReason();
  const { organizationId, intakeId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const intakesRepository = new IntakesRepository();
  const intakeFilesTableRef = useRef<IntakeFilesTableRef>(null);
  const [intakeEntity, setIntake] = useState<Intake | undefined>(undefined);
  const [dirtyFlagStatus, setDirtyFlagStatus] = useState<MigrationFlagStatus>();
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [isConfirmExportModalOpen, setIsConfirmExportModalOpen] = React.useState<boolean>(false);
  const [newIntakeFilesForDeletion, setNewIntakeFilesForDeletion] = useState<IntakeFile[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveJobsExplorer>(ActiveJobsExplorer.Export);
  const [viewMode, setViewMode] = useState<"files" | "folders">("files");
  const queuedCount = newIntakeFilesForDeletion.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);
  const { setEntityLabel } = useDetailBreadcrumb();

  const intakeQuery = useQuery({ queryKey: [QUERY_KEYS.INTAKE, intakeId], queryFn:
    async () => await intakesRepository.getIntake(intakeId ?? ""), gcTime: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_CACHE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_REFETCH_INTERVAL,
      refetchOnMount: true, });

  useEffect(() => {
    if (intakeQuery.data) {
      setIntake(intakeQuery.data);
      setEntityLabel(`Intake ${intakeQuery.data.intakeNumber}`);
    }
    return () => setEntityLabel(null);
  }, [intakeQuery.data, intakeId]);

  if (intakeQuery.isLoading || (!intakeQuery.isError && !intakeEntity))
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    );

  if (intakeQuery.isError)
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Failed to load intake.</p>
        <Button variant="outline" size="sm" onClick={() => intakeQuery.refetch()}>
          Retry
        </Button>
      </div>
    );

  if (!intakeEntity) return null;

  const handleFlagStatusModal = (flagStatus: MigrationFlagStatus, isOpen: boolean) => {
    setIsModalOpen(isOpen);
    setDirtyFlagStatus(flagStatus);
  };

  const startCurrentViewExportToCsv = (reason?: string) =>
    intakeFilesTableRef.current?.exportCurrentViewToCsv(reason);

  const migrationFlagStatusSelectOption =
    MigrationFlagStatusSelectOptions.find((option) => option.id === intakeEntity?.flagStatus) || MigrationFlagStatusSelectOptions[0];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="w-full my-2 gap-2 flex-wrap shrink-0">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-start">
            <h2 className="text-3xl font-bold tracking-tight">
              Intake {intakeEntity.intakeNumber}{" "}
              <MigrationStatusChip status={intakeEntity.status} />
            </h2>
            <div className="flex flex-row gap-2">
              {userRole !== UserRole.Viewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  className="gap-2"
                >
                  <Settings2 className="size-4" />
                  Manage
                  {queuedCount > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5">
                      {queuedCount}
                    </Badge>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/organization/${organizationId}/intakes`)}
              >
                Back
              </Button>
            </div>
          </div>
          <div className="flex flex-row w-full flex-1 justify-between items-center gap-2">
            <MigrationEntityShortStatistics
              totalNumberOfFiles={intakeEntity.statistics.totalNumberOfFiles ?? 0}
              filesMigrated={intakeEntity.statistics.filesMigrated ?? 0}
              filesFlagged={intakeEntity.statistics.filesFlagged ?? 0}
              filesBlockedByEntityFlag={(intakeEntity.statistics.filesInEntityWithOver10KFiles ?? 0) + (intakeEntity.statistics.filesInEntityBlockedByUser ?? 0)}
              filesInterruptedInitial={intakeEntity.statistics.filesInterruptedInitial ?? 0}
              filesInterruptedDelta={intakeEntity.statistics.filesInterruptedDelta ?? 0}
              filesModified={intakeEntity.statistics.filesModified ?? 0}
              filesNotFound={intakeEntity.statistics.filesNotFound ?? 0}
              filesNotFoundMarkedForRetry={intakeEntity.statistics.filesNotFoundMarkedForRetry ?? 0}
              unresolvedConflictCount={intakeEntity.statistics.unresolvedConflictCount ?? 0}
              acknowledgedConflictCount={intakeEntity.statistics.acknowledgedConflictCount ?? 0}
              filesSizeInMb={intakeEntity.statistics.filesSizeInMb ?? 0}
            />
            <div className="flex flex-row gap-2 items-center">
              {intakeEntity.flagType && (
                <Select
                  value={migrationFlagStatusSelectOption.id}
                  onValueChange={(value) => handleFlagStatusModal(value as MigrationFlagStatus, true)}
                  disabled={
                    userRole === UserRole.Viewer
                    || (!isAssignedUser && userRole !== UserRole.Admin)
                  }
                >
                  <SelectTrigger
                    className={cn(
                      "w-[260px]",
                      migrationFlagStatusSelectOption.color && colorVariantClasses[migrationFlagStatusSelectOption.color]?.text
                    )}
                  >
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {MigrationFlagStatusSelectOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        <span className={cn(option.color && colorVariantClasses[option.color]?.text)}>
                          {option.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 mt-2">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          {viewMode === "files" ? (
            <IntakeFilesTable
              ref={intakeFilesTableRef}
              organizationId={organizationId ?? ""}
              intakeId={intakeId ?? ""}
              activeJobsExplorer={selectedTab}
              newIntakeFilesForDeletion={newIntakeFilesForDeletion}
              setNewIntakesFilesForDeletion={setNewIntakeFilesForDeletion}
              disabled={userRole === UserRole.Viewer}
              toolbar={
                <div className="flex w-full items-center gap-2 my-1">
                  <ToggleGroup type="single" value={viewMode} onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }} variant="outline" size="sm" className="flex-1">
                    <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                    <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                  </ToggleGroup>
                  {intakeEntity.hasFlaggedPaths && (
                    <FlaggedPathResolveView
                      organizationId={organizationId ?? ""}
                      migrationEntityId={intakeEntity.intakeNumber}
                      migrationType={MigrationType.IntakesMigration}
                      entityNoun="intake"
                      invalidateQueryKeys={[[QUERY_KEYS.INTAKE]]}
                    />
                  )}
                  {userRole !== UserRole.Viewer && (
                    <ConflictResolveView
                      parentId={intakeId ?? ""}
                      entityNoun="intake"
                      groupsQueryKey={[QUERY_KEYS.INTAKE_CONFLICT_GROUPS, intakeId]}
                      invalidateQueryKeys={[[QUERY_KEYS.INTAKE_FILES], [QUERY_KEYS.INTAKE, intakeId]]}
                      fetchGroups={(id, includeResolved) => intakesRepository.getConflictGroups(id, includeResolved)}
                      resolveGroup={(resolutions) => intakesRepository.resolveConflictGroup(resolutions)}
                    />
                  )}
                </div>
              }
            />
          ) : (
            <IntakeEmptyFoldersTable
              intakeId={intakeId ?? ""}
              toolbar={
                <ToggleGroup type="single" value={viewMode} onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }} variant="outline" size="sm" className="w-full my-1">
                  <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                  <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                </ToggleGroup>
              }
            />
          )}
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage files</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveJobsExplorer.Delete)} className="flex-1">Delete files</ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveJobsExplorer.Export)} className="flex-1">Export CSV</ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 overflow-auto min-h-0">
                {selectedTab === ActiveJobsExplorer.Delete && (userRole === UserRole.Admin || isAssignedUser) && (
                  <div className="flex flex-col h-full gap-4">
                    {newIntakeFilesForDeletion.length > 0 && (
                      <>
                        <div className="flex items-center gap-3 py-2 pt-4">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                          <Separator className="flex-1" />
                        </div>
                        <CreateDeleteFilesRequestTable
                          organizationId={organizationId ?? ""}
                          entityId={intakeId ?? ""}
                          entityKey={intakeEntity.intakeNumber}
                          newFilesForDeletion={newIntakeFilesForDeletion}
                          setNewFilesForDeletion={setNewIntakeFilesForDeletion}
                          fileTypeConfig={CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS.INTAKE_FILES_CONFIG}
                        />
                        <div className="flex items-center gap-3 py-2">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>Deletion status</b></span>
                          <Separator className="flex-1" />
                        </div>
                      </>
                    )}
                    <DeleteIntakeFilesStatusTable intakeId={intakeId ?? ""}/>
                  </div>
                )}
                {selectedTab === ActiveJobsExplorer.Export && (
                  <div className="flex flex-col h-full gap-4">
                    <div className="pt-4 w-full flex-1">
                      {requiresReason ? (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.EXPORT_INTAKE_FILES_VIEW}
                          onConfirm={async (reason) => {
                            startCurrentViewExportToCsv(reason);
                          }}
                        >
                          <Button>Export current files view</Button>
                        </ActionConfirmationModal>
                      ) : (
                        <Button onClick={() => setIsConfirmExportModalOpen(true)}>
                          Export current files view
                        </Button>
                      )}
                      <ExportIntakeFilesToCsvStatusTable
                        intakeId={intakeId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                        organizationId={organizationId ?? ""}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {dirtyFlagStatus && intakeEntity.flagType && (
        <UpdateIntakeFlagModal
          open={isModalOpen}
          flagStatus={dirtyFlagStatus}
          intakeNumber={intakeEntity.intakeNumber}
          onClose={() => {
            handleFlagStatusModal(MigrationFlagStatus.UnResolved, false);
          }}
          onConfirm={async () => {
            if (intakeId !== undefined) {
              await intakesRepository.updateIntakeFlag(intakeEntity.id, dirtyFlagStatus);
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTAKES, organizationId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTAKE, intakeEntity.id] });
              handleFlagStatusModal(MigrationFlagStatus.UnResolved, false);
            }
          }}
        />
      )}
      {isConfirmExportModalOpen && (
        <ConfirmationModal
          open={isConfirmExportModalOpen}
          onClose={() => {setIsConfirmExportModalOpen(false);}}
          onConfirm={async () => {
            startCurrentViewExportToCsv();
            setIsConfirmExportModalOpen(false);
          }}
          message="Are you sure you want to export intake files from the current view?"
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};
